import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftItemForStoreDataAtomFamily,
  giftItemForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  giftStoreNavBarScrolledAtom,
  giftStoreNavBarTitleAtom,
  giftStoreNavBarTransparentAtom,
} from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { giftCartAtom, GiftCartItem } from '../state/giftCart.atom';

const GiftItemDetailPage: FC = function GiftItemDetailPage() {
  const { itemId } = useParams<{ itemId?: string }>();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');
  const navigate = useNavigate();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const setTransparent = useSetRecoilState(giftStoreNavBarTransparentAtom);
  const setScrolled = useSetRecoilState(giftStoreNavBarScrolledAtom);
  const setCartItems = useSetRecoilState(giftCartAtom);

  // 투명 모드 활성화 (마운트 시)
  useEffect(() => {
    setTitle(' ');
    setTransparent(true);
    setScrolled(false);

    // 언마운트 시 원래대로 복원
    return () => {
      setTransparent(false);
      setScrolled(false);
    };
  }, [setTitle, setTransparent, setScrolled]);
  // 상품 조회
  useGiftItemForStoreHook(itemId ?? '');
  const status = useRecoilValue(giftItemForStoreStatusAtomFamily(itemId ?? ''));
  const item = useRecoilValue(giftItemForStoreDataAtomFamily(itemId ?? ''));

  // Carousel 상태
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 수량 상태
  const [quantity, setQuantity] = useState(1);

  // 최소주문수량, 주문단위
  const minOrderQuantity = useMemo(() => {
    return item?.giftItemInfo?.minOrderQuantity || 1;
  }, [item]);

  const orderUnit = useMemo(() => {
    return item?.giftItemInfo?.orderUnit || 1;
  }, [item]);

  // 초기 수량 설정
  useEffect(() => {
    if (minOrderQuantity > 0) {
      setQuantity(minOrderQuantity);
    }
  }, [minOrderQuantity]);

  // 상품명 (오버라이드 우선)
  const productName = useMemo(() => {
    return item?.name || item?.giftItemInfo?.name || '';
  }, [item]);

  // 스크롤 감지 - 이미지 절반 지점에서 헤더 스타일 변경
  useEffect(() => {
    const handleScroll = () => {
      // 이미지는 aspect-square이므로 화면 너비 = 이미지 높이
      const imageHeight = window.innerWidth;
      const scrollThreshold = imageHeight / 2;
      const isScrolledPast = window.scrollY > scrollThreshold;

      setScrolled(isScrolledPast);

      // 스크롤 후에는 상품명을 타이틀로 표시
      if (isScrolledPast && productName) {
        setTitle(productName);
      } else {
        setTitle(' ');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrolled, setTitle, productName]);

  // 헤더 이미지 (오버라이드 우선)
  const headerImages = useMemo(() => {
    if (item?.headerImages && item.headerImages.length > 0) {
      return item.headerImages as Image[];
    }
    if (item?.giftItemInfo?.headerImages && item.giftItemInfo.headerImages.length > 0) {
      return item.giftItemInfo.headerImages as Image[];
    }
    // headerImages가 없으면 대표 이미지 사용
    const mainImage = item?.image || item?.giftItemInfo?.image;
    if (mainImage && typeof mainImage === 'object') {
      return [mainImage as Image];
    }
    return [];
  }, [item]);

  // 스와이프 감지 (최소 50px 이동)
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // 왼쪽으로 스와이프 → 다음 이미지
      setCurrentImageIndex((prev) => (prev === headerImages.length - 1 ? 0 : prev + 1));
    } else if (isRightSwipe) {
      // 오른쪽으로 스와이프 → 이전 이미지
      setCurrentImageIndex((prev) => (prev === 0 ? headerImages.length - 1 : prev - 1));
    }
  }, [touchStart, touchEnd, headerImages.length]);

  // 상세 이미지 (오버라이드 우선)
  const detailImages = useMemo(() => {
    if (item?.detailImages && item.detailImages.length > 0) {
      return item.detailImages as Image[];
    }
    if (item?.giftItemInfo?.detailImages && item.giftItemInfo.detailImages.length > 0) {
      return item.giftItemInfo.detailImages as Image[];
    }
    return [];
  }, [item]);

  // 상품 타입
  const productType = useMemo(() => {
    return item?.giftItemInfo?.type || GiftItemInfoType.Delivery;
  }, [item]);

  // 쿠폰 상세
  const couponDetail = useMemo(() => {
    return item?.giftItemInfo?.couponDetail;
  }, [item]);

  // 브랜드 이름
  const brandName = useMemo(() => {
    const brand = item?.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return null;
  }, [item]);

  // 선택형 상품 여부 및 선택 옵션
  const isSelectable = useMemo(() => {
    return item?.giftItemInfo?.isSelectable || false;
  }, [item]);

  const choiceUnits = useMemo(() => {
    const units = item?.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return [];
    // populate된 객체인지 확인하고 order 순으로 정렬
    return units
      .filter(
        (unit): unit is GiftChoiceUnitData => typeof unit === 'object' && unit !== null,
      )
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  }, [item]);

  // 상품 설명 (오버라이드 우선)
  const detailInfo = useMemo(() => {
    return item?.detailInfo || item?.giftItemInfo?.detailInfo || '';
  }, [item]);

  // 가격
  const priceRegular = item?.priceRegular || 0;
  const priceRetail = item?.priceRetail || 0;
  const discountRate =
    priceRegular > 0
      ? Math.round(((priceRegular - priceRetail) / priceRegular) * 100)
      : 0;

  // 수량 증가
  const increaseQuantity = useCallback(() => {
    setQuantity((prev) => prev + orderUnit);
  }, [orderUnit]);

  // 수량 감소
  const decreaseQuantity = useCallback(() => {
    setQuantity((prev) => {
      const newQty = prev - orderUnit;
      return newQty >= minOrderQuantity ? newQty : prev;
    });
  }, [orderUnit, minOrderQuantity]);

  // 수량 직접 입력
  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setQuantity(value);
    }
  }, []);

  // 입력 완료 시 최소수량/주문단위 조정
  const handleQuantityBlur = useCallback(() => {
    setQuantity((prev) => {
      // 최소주문수량 이하면 최소주문수량으로
      if (prev < minOrderQuantity) {
        return minOrderQuantity;
      }
      // 주문단위에 맞게 조정 (올림)
      if (orderUnit > 1) {
        const remainder = (prev - minOrderQuantity) % orderUnit;
        if (remainder !== 0) {
          return prev + (orderUnit - remainder);
        }
      }
      return prev;
    });
  }, [minOrderQuantity, orderUnit]);

  // 대표 이미지 URL
  const mainImageUrl = useMemo(() => {
    const img = item?.image || item?.giftItemInfo?.image;
    if (img && typeof img === 'object' && 'url' in img) {
      return (img as Image).url;
    }
    return '';
  }, [item]);

  // 장바구니 담기
  const handleAddToCart = useCallback(() => {
    if (!itemId || !bugoId) return;

    setCartItems((prev) => {
      // 같은 상품이 있는지 확인
      const existingItem = prev.find((cartItem) => cartItem.itemId === itemId);

      if (existingItem) {
        // 있으면 수량 업데이트
        return prev.map((cartItem) =>
          cartItem.itemId === itemId ? { ...cartItem, quantity } : cartItem,
        );
      } else {
        // 없으면 새로 추가
        const newCartItem: GiftCartItem = {
          itemId: itemId,
          itemName: productName,
          itemImage: mainImageUrl,
          priceRetail,
          quantity,
          minOrderQuantity,
          orderUnit,
          bugoId: bugoId,
        };
        return [...prev, newCartItem];
      }
    });

    alert(`'${productName}' ${quantity}개가 장바구니에 담겼습니다.`);
  }, [
    itemId,
    bugoId,
    productName,
    mainImageUrl,
    priceRetail,
    quantity,
    minOrderQuantity,
    orderUnit,
    setCartItems,
  ]);

  // 바로 구매
  const handleBuyNow = useCallback(() => {
    navigate(`/gift-store/item/${itemId}/checkout?bugoId=${bugoId}&quantity=${quantity}`);
  }, [navigate, itemId, bugoId, quantity]);

  // 총 금액
  const totalPrice = priceRetail * quantity;

  if (status !== 'success' || !item) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-26">
      {/* 헤더 이미지 Carousel */}
      {headerImages.length > 0 && (
        <div className="relative">
          <div
            className="aspect-square w-full overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="flex h-full transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {headerImages.map((img, idx) => (
                <img
                  key={idx}
                  src={fileBucketUrl(img?.url ?? '', 'raw')}
                  alt={`${productName} ${idx + 1}`}
                  className="h-full w-full flex-shrink-0 object-cover"
                />
              ))}
            </div>
          </div>

          {/* Carousel 네비게이션 */}
          {headerImages.length > 1 && (
            <>
              {/* 좌우 버튼 */}
              <button
                onClick={() =>
                  setCurrentImageIndex((prev) =>
                    prev === 0 ? headerImages.length - 1 : prev - 1,
                  )
                }
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white"
              >
                ◀
              </button>
              <button
                onClick={() =>
                  setCurrentImageIndex((prev) =>
                    prev === headerImages.length - 1 ? 0 : prev + 1,
                  )
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white"
              >
                ▶
              </button>

              {/* 인디케이터 */}
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                {headerImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 상품 정보 */}
      <div className="p-4">
        {/* 브랜드 & 타입 뱃지 */}
        <div className="flex items-center gap-2">
          {brandName && (
            <span className="inline-block rounded bg-orange-500 px-2 py-0.5 text-xs font-bold text-white">
              {brandName}
            </span>
          )}
          {productType === GiftItemInfoType.Delivery && (
            <span className="inline-block rounded bg-blue-500 px-2 py-0.5 text-xs font-bold text-white">
              배송형
            </span>
          )}
        </div>

        {/* 상품명 */}
        <h1 className="mt-2 text-xl font-bold">{productName}</h1>

        {/* 가격 */}
        <div className="mt-3 flex items-end gap-2">
          {discountRate > 0 && (
            <>
              <span className="text-2xl font-bold text-red-500">{discountRate}%</span>
              <span className="text-lg text-gray-400 line-through">
                {formatPrice(priceRegular)}원
              </span>
            </>
          )}
          <span className="text-2xl font-bold">{formatPrice(priceRetail)}원</span>
        </div>

        {/* 쿠폰 정보 (쿠폰형일 때) */}
        {productType === GiftItemInfoType.Coupon && couponDetail && (
          <div className="mt-4 rounded-lg bg-purple-50 p-4">
            <h3 className="font-bold text-purple-700">쿠폰 정보</h3>
            <div className="mt-2 space-y-1 text-sm">
              {couponDetail.validPeriod && (
                <div className="flex">
                  <span className="w-16 text-gray-500">유효기간</span>
                  <span className="font-medium">{couponDetail.validPeriod}</span>
                </div>
              )}
              {couponDetail.placeOfExchange && (
                <div className="flex">
                  <span className="w-16 text-gray-500">사용처</span>
                  <span className="font-medium">{couponDetail.placeOfExchange}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 선택 가능 옵션 (선택형일 때) */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-4 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
            <h3 className="font-bold text-indigo-700">
              선택 가능한 옵션 ({choiceUnits.length}종)
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              아래 옵션 중 하나를 선택하여 받으실 수 있습니다
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {choiceUnits.map((unit) => {
                // choice unit의 brand 이름
                const unitBrandName =
                  unit.giftItemBrand &&
                  typeof unit.giftItemBrand === 'object' &&
                  'name' in unit.giftItemBrand
                    ? unit.giftItemBrand.name
                    : null;

                return (
                  <div
                    key={unit._id}
                    className="flex flex-col items-center rounded-lg bg-white p-2 shadow-sm"
                  >
                    {/* 이미지 */}
                    <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
                      {unit.image ? (
                        <img
                          src={fileBucketUrl(
                            typeof unit.image === 'object' ? unit.image.url : unit.image,
                            'w200',
                          )}
                          alt={unit.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                          No Image
                        </div>
                      )}
                    </div>
                    {/* 브랜드 */}
                    {unitBrandName && (
                      <span className="mt-1.5 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                        {unitBrandName}
                      </span>
                    )}
                    {/* 이름 */}
                    <span className="line-clamp-2 mt-1 break-keep text-center text-xs font-medium text-gray-800">
                      {unit.name}
                    </span>
                    {/* 정가 */}
                    {unit.priceRegular && (
                      <span className="mt-1 text-xs text-indigo-600">
                        정가 {formatPrice(unit.priceRegular)}원
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 상품 설명 */}
        {detailInfo && (
          <div className="mt-6">
            <h3 className="font-bold text-gray-700">상품 설명</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{detailInfo}</p>
          </div>
        )}

        {/* 주문 정보 */}
        <div className="mt-6 rounded-lg bg-gray-50 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">최소 주문수량</span>
            <span className="font-medium">{minOrderQuantity}개</span>
          </div>
          {orderUnit > 1 && (
            <div className="mt-1 flex justify-between">
              <span className="text-gray-500">주문 단위</span>
              <span className="font-medium">{orderUnit}개씩</span>
            </div>
          )}
        </div>
      </div>

      {/* 상세 이미지 */}
      {detailImages.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <h3 className="px-4 font-bold text-gray-700">상세 정보</h3>
          <div className="mt-2">
            {detailImages.map((img, idx) => (
              <img
                key={idx}
                src={fileBucketUrl(img.url, 'raw')}
                alt={`상세이미지 ${idx + 1}`}
                className="w-full"
              />
            ))}
          </div>
        </div>
      )}

      {/* 하단 고정 영역 */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white px-3 py-2 shadow-lg">
        <div className="mx-auto max-w-lg space-y-2">
          {/* 수량 조절 (상단 한 줄) */}
          <div className="relative flex items-center justify-center">
            <span className="absolute left-2 text-sm text-gray-600">수량</span>
            <div className="flex items-center">
              <button
                onClick={decreaseQuantity}
                disabled={quantity <= minOrderQuantity}
                className="flex h-10 w-14 items-center justify-center rounded-lg border-2 border-gray-300 bg-gray-100 text-2xl font-bold text-gray-700 active:bg-gray-300 disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-300"
              >
                −
              </button>
              <input
                type="number"
                inputMode="numeric"
                value={quantity}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                className="w-[72px] bg-transparent text-center text-lg font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                onClick={increaseQuantity}
                className="flex h-10 w-14 items-center justify-center rounded-lg border-2 border-gray-300 bg-gray-100 text-2xl font-bold text-gray-700 active:bg-gray-300"
              >
                +
              </button>
            </div>
            {orderUnit > 1 && (
              <span className="absolute right-2 text-xs text-gray-400">
                ({orderUnit}개 단위)
              </span>
            )}
          </div>

          {/* 하단: 총금액 + 버튼들 */}
          <div className="flex items-center gap-2">
            {/* 총 금액 */}
            <div className="flex-1">
              <span className="text-xs text-gray-500">총 금액</span>
              <p className="text-base font-bold text-red-500">
                {formatPrice(totalPrice)}원
              </p>
            </div>

            {/* 버튼들 */}
            <button
              onClick={handleAddToCart}
              className="rounded-lg border-2 border-gray-800 px-5 py-2.5 text-base font-bold text-gray-800 active:bg-gray-100"
            >
              장바구니
            </button>
            <button
              onClick={handleBuyNow}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-base font-bold text-white active:bg-red-600"
            >
              바로구매
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftItemDetailPage;
